import { useClerk, useReverification } from "@clerk/tanstack-react-start";
import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/tanstackstart-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Mail, Phone, Upload, User, UserCircle, Trash2 } from "lucide-react";
import { motion } from "motion/react";
import React from "react";
import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { CloseAccountCard } from "~/components/close-account-card";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { FormField } from "~/components/ui/form-field";
import { Input } from "~/components/ui/input";
import { OtpInput } from "~/components/ui/otp-input";
import { Textarea } from "~/components/ui/textarea";
import { UploadAvatarModal } from "~/components/upload-avatar-modal";
import { getClerkErrorMessage } from "~/lib/clerk-errors";
import { cn } from "~/lib/cn";
import { confirmEmailChange } from "~/lib/functions/user";
import {
  userProfileQueryOptions,
  useUpdateUserProfile,
} from "~/lib/queries/userQuery";
import {
  invalidProfileFieldLabels,
  UserProfileSchema,
} from "~/lib/schemas/user-auth";
import type { UserProfileForm } from "~/lib/schemas/user-auth";
import { createNoIndexMeta } from "~/lib/shared/meta";
import { UserFacingError } from "~/lib/shared/user-facing-error";

import { OnboardingModal } from "./-components/onboarding-modal";
import { ProfileSettingsSkeleton } from "./-components/profile-settings-skeleton";
import { SettingsCardTitle } from "./-components/settings-card-title";

export const Route = createFileRoute("/_user/settings/profile/")({
  validateSearch: z.object({ onboarding: z.boolean().optional() }),
  head: () =>
    createNoIndexMeta(
      "Profile Settings – Dream Coach Match",
      "Update your profile information",
    ),
  loader: async ({ context }) => {
    const userId = context.user?.id;
    if (!userId) {
      throw new Error("User not authenticated");
    }
    await context.queryClient.ensureQueryData(userProfileQueryOptions(userId));
    return {};
  },
  component: ProfileSettingsPage,
});

/** "email", "email and title", "email, title, and description". */
function formatFieldList(labels: string[]): string {
  return new Intl.ListFormat("en", {
    style: "long",
    type: "conjunction",
  }).format(labels);
}

export function ProfileSettingsPage() {
  const clerk = useClerk();
  const userId = clerk.user?.id;
  const { onboarding } = Route.useSearch();

  const { data: profileData } = useQuery(userProfileQueryOptions(userId || ""));
  const showOnboarding =
    onboarding ||
    (profileData !== undefined &&
      !profileData.firstName &&
      !profileData.lastName);

  const [emailId, setEmailId] = useState<string>();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showOtpField, setShowOtpField] = useState(false);
  const [isPhoneProcessing, setIsPhoneProcessing] = useState(false);
  const [isRemovingPhoto, setIsRemovingPhoto] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const [state, setState] = useState({
    code: ["", "", "", "", "", ""],
    error: "",
    otpError: "",
    resendTimer: 60,
    canResend: false,
  });

  const [phoneError, setPhoneError] = useState("");

  const {
    handleSubmit,
    formState,
    watch,
    getValues,
    setError,
    setValue,
    control,
    reset,
    trigger,
  } = useForm<UserProfileForm>({
    resolver: zodResolver(UserProfileSchema),
    defaultValues: profileData || {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: undefined,
      photo: undefined,
      title: undefined,
      bio: undefined,
      clerkUserId: undefined,
    },
  });

  const { errors } = formState;

  const createEmailAddressWithReverification = useReverification(
    async (email: string) => {
      if (!clerk.user) {
        throw new Error("User not authenticated");
      }
      return await clerk.user.createEmailAddress({ email });
    },
  );

  const updateProfileMutation = useUpdateUserProfile({
    onSuccess: () => {
      toast.success("Profile updated");
    },
    onError: (error: Error) => {
      setError("root", {
        message: error.message || "Failed to update profile",
      });
      toast.error("Failed to update profile");
    },
  });

  // The avatar upload saves the profile as a side effect but reports its own
  // failure inside the modal, so it saves through its own mutation with no
  // error handler rather than muting the one above — a page-wide "don't toast"
  // flag would also silence a form save that happened to fail while an upload
  // was in flight. Success still toasts: the modal only closes, so nothing
  // else confirms the save.
  const saveAvatarMutation = useUpdateUserProfile({
    onSuccess: () => {
      toast.success("Profile updated");
    },
  });

  useEffect(() => {
    if (profileData) {
      reset(profileData);
    }
  }, [profileData, reset]);

  if (clerk.loaded && !userId) {
    throw new Error("User not authenticated");
  }

  if (!clerk.loaded || !userId) {
    return <ProfileSettingsSkeleton />;
  }

  const startResendTimer = () => {
    setState((prev) => ({
      ...prev,
      resendTimer: 60,
      canResend: false,
    }));

    const timer = setInterval(() => {
      setState((prev) => {
        const newTimer = prev.resendTimer - 1;
        if (newTimer <= 0) {
          clearInterval(timer);
          return { ...prev, resendTimer: 0, canResend: true };
        }
        return { ...prev, resendTimer: newTimer };
      });
    }, 1000);
  };

  const handleSendOtp = async () => {
    setState((prev) => ({ ...prev, error: "", otpError: "" }));

    try {
      await handleSubmit(() => {
        /* No operation needed */
      })();
    } catch {
      return;
    }

    startResendTimer();
    setIsProcessing(true);

    try {
      if (!clerk?.client) {
        return;
      }
      if (!clerk.user?.id) {
        setState((prev) => ({ ...prev, error: "User not authenticated" }));
        return;
      }

      const email = getValues("email");

      // Check if email already exists in user's email addresses
      const existingEmail = clerk.user.emailAddresses.find(
        (emailAddr) => emailAddr.emailAddress === email,
      );

      if (existingEmail) {
        // If email exists but not verified, resend verification
        if (existingEmail.verification?.status !== "verified") {
          setEmailId(existingEmail.id);
          await existingEmail.prepareVerification({ strategy: "email_code" });
          setShowOtpField(true);
        } else {
          // Email is already verified - make it primary instead of showing error
          await confirmEmailChange({
            data: { emailAddressId: existingEmail.id },
          });
          handleSubmit(onSubmit)();
          setState((prev) => ({ ...prev, error: "" }));
          setShowOtpField(false); // Hide OTP field since no verification needed
        }
      } else {
        // Create new email address and prepare verification.
        // Adding an email is a sensitive action that Clerk gates behind
        // reverification — useReverification triggers Clerk's prebuilt
        // verification modal, then retries the call. Returns null if the
        // user cancels.
        const res = await createEmailAddressWithReverification(email);
        if (res === null) {
          setIsProcessing(false);
          return;
        }
        setEmailId(res.id);
        await res.prepareVerification({ strategy: "email_code" });
        setShowOtpField(true);
      }

      setIsProcessing(false);
    } catch (error) {
      setIsProcessing(false);
      const message = getClerkErrorMessage(
        error,
        "Failed to create sign up attempt",
      );
      setState((prev) => ({ ...prev, error: message }));
    }
  };

  const handleVerifyOtp = async (codeToVerify?: string) => {
    setState((prev) => ({ ...prev, otpError: "" }));

    const verificationCode = codeToVerify || state.code.join("");

    if (!verificationCode || verificationCode.length !== 6) {
      setState((prev) => ({
        ...prev,
        otpError: "Please enter a complete 6-digit code",
      }));
      return;
    }

    try {
      if (!clerk?.client) {
        return;
      }
      if (!clerk?.user?.id) {
        setState((prev) => ({ ...prev, error: "User not authenticated" }));
        return;
      }

      if (!emailId) {
        setState((prev) => ({
          ...prev,
          error: "No email verification in progress",
        }));
        return;
      }

      const emailToVerify = clerk.user.emailAddresses.find(
        (email) => email.id === emailId,
      );

      if (!emailToVerify) {
        setState((prev) => ({ ...prev, error: "Email address not found" }));
        return;
      }

      await emailToVerify.attemptVerification({
        code: verificationCode,
      });

      // After successful verification, make it the account's email
      await confirmEmailChange({ data: { emailAddressId: emailId } });
      handleSubmit(onSubmit)();
      setShowOtpField(false);
      setState((prev) => ({ ...prev, code: ["", "", "", "", "", ""] }));
    } catch (error) {
      const message = getClerkErrorMessage(
        error,
        "Invalid verification code. Please try again.",
      );
      setState((prev) => ({
        ...prev,
        otpError: message,
      }));
    }
  };

  const handleOtpChange = (code: string[]) => {
    setState((prev) => ({ ...prev, code: [...code], otpError: "" }));
  };

  const handleOtpComplete = (code: string) => {
    setState((prev) => ({ ...prev, code: code.split("") }));
    handleVerifyOtp(code);
  };

  const handleUpdatePhone = async () => {
    setPhoneError("");
    setIsPhoneProcessing(true);

    try {
      const phoneNumber = getValues("phoneNumber");
      if (!phoneNumber) {
        setPhoneError("Please enter a phone number");
        setIsPhoneProcessing(false);
        return;
      }

      const isValid = await trigger();
      if (!isValid) {
        setPhoneError("Please fix the errors in the form before saving.");
        setIsPhoneProcessing(false);
        return;
      }

      // Save phone number to database
      updateProfileMutation.mutate({
        firstName: getValues("firstName"),
        lastName: getValues("lastName"),
        email: getValues("email"),
        phoneNumber: phoneNumber,
        photo: getValues("photo"),
        title: getValues("title"),
        bio: getValues("bio"),
      });

      setIsPhoneProcessing(false);
    } catch {
      setIsPhoneProcessing(false);
      setPhoneError("Failed to update phone number");
    }
  };

  const onSubmit = (data: UserProfileForm) => {
    updateProfileMutation.mutate(data);
  };

  const handleOpenUpload = () => {
    setIsUploadModalOpen(true);
  };
  /**
   * Runs inside the upload modal, which shows whatever `UserFacingError` this
   * throws and reports anything else to Sentry. So every outcome the user can
   * put right — and every failure already reported elsewhere — leaves here as a
   * `UserFacingError`, and only an unexpected one escapes as a plain Error.
   */
  const handleUploadSave = async (file: File) => {
    if (!clerk.user) {
      throw new UserFacingError(
        "You're signed out. Sign in again to change your photo.",
      );
    }

    // Validate before writing to Clerk: the save below sends the whole
    // UserProfileSchema, so an invalid name/email fails it — and a Clerk
    // write that already happened would leave Clerk showing the new avatar
    // while our own profile still shows the old one.
    const isValid = await trigger();
    if (!isValid) {
      const invalidFields = invalidProfileFieldLabels(getValues());
      throw new UserFacingError(
        invalidFields.length > 0
          ? `Please fix your ${formatFieldList(invalidFields)} above before uploading a photo.`
          : "Please fix the errors in the form above before uploading a photo.",
      );
    }

    let imageUrl: string | null;
    try {
      const updatedImage = await clerk.user.setProfileImage({ file });
      imageUrl = updatedImage.publicUrl;
    } catch (error) {
      // The only failure on this path Sentry doesn't already hear about: the
      // profile save below is a mutation, so MutationCache reports it centrally
      // (see reportMutationError).
      Sentry.captureException(error, { tags: { surface: "avatar_upload" } });
      throw new UserFacingError(
        getClerkErrorMessage(error, "Failed to update profile photo."),
        { cause: error },
      );
    }

    if (!imageUrl) {
      throw new Error("Failed to retrieve new profile photo URL from Clerk.");
    }

    setValue("photo", imageUrl, { shouldDirty: true });

    try {
      await saveAvatarMutation.mutateAsync({
        ...getValues(),
        photo: imageUrl,
      });
    } catch (error) {
      throw new UserFacingError(
        getClerkErrorMessage(error, "Failed to save your new profile photo."),
        { cause: error },
      );
    }
  };

  const handleUploadClose = () => {
    setIsUploadModalOpen(false);
  };

  const handleRemovePhoto = async () => {
    if (!clerk.user) {
      toast.error("User not authenticated");
      return;
    }

    setIsRemovingPhoto(true);

    try {
      const isValid = await trigger();
      if (!isValid) {
        toast.error("Please fix the errors in the form before saving.");
        return;
      }

      await updateProfileMutation.mutateAsync({
        ...getValues(),
        photo: "",
        removePhoto: true,
      });

      setValue("photo", "", { shouldDirty: false });
      toast.success("Photo removed");
    } catch (error) {
      const message = getClerkErrorMessage(
        error,
        "Failed to remove profile photo.",
      );
      toast.error(message);
    } finally {
      setIsRemovingPhoto(false);
    }
  };

  return (
    <>
      {showOnboarding && <OnboardingModal />}
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {/* Personal details & Profile details */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="grid grid-cols-1 gap-4 lg:grid-cols-2"
        >
          <Card>
            <SettingsCardTitle icon={User}>Personal details</SettingsCardTitle>

            <div className="flex items-center gap-4">
              <div className="relative">
                <Avatar className="h-20 w-20 rounded-full ring-2 ring-gray-100 ring-offset-2">
                  <AvatarImage
                    src={
                      watch("photo") !== ""
                        ? watch("photo") || profileData?.photo
                        : undefined
                    }
                  />
                  <AvatarFallback className="rounded-full bg-purple-50 text-sm font-medium text-gray-900">
                    <User size={32} className="text-purple-400" />
                  </AvatarFallback>
                </Avatar>
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  size="sm"
                  className="gap-1.5"
                  onClick={handleOpenUpload}
                >
                  <Upload className="size-3.5" />
                  Upload photo
                </Button>

                {(watch("photo") !== ""
                  ? watch("photo") || profileData?.photo
                  : false) && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="text-text-tertiary gap-1.5 hover:text-red-600"
                    onClick={handleRemovePhoto}
                    loading={isRemovingPhoto}
                    disabled={isRemovingPhoto}
                  >
                    <Trash2 className="size-3.5" />
                    Remove
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField label="First name" error={errors.firstName?.message}>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ""}
                      error={errors.firstName?.message}
                      placeholder="Enter your first name"
                    />
                  )}
                />
              </FormField>
              <FormField label="Last name" error={errors.lastName?.message}>
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ""}
                      error={errors.lastName?.message}
                      placeholder="Enter your last name"
                    />
                  )}
                />
              </FormField>
            </div>
          </Card>

          <Card>
            <SettingsCardTitle icon={UserCircle}>
              Profile details
            </SettingsCardTitle>

            <FormField label="Title" error={errors.title?.message}>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    value={field.value || ""}
                    error={errors.title?.message}
                    placeholder="Enter your title"
                  />
                )}
              />
            </FormField>

            <FormField label="Description" error={errors.bio?.message}>
              <Controller
                name="bio"
                control={control}
                render={({ field }) => (
                  <Textarea
                    rows={5}
                    {...field}
                    value={field.value || ""}
                    error={errors.bio?.message}
                    placeholder="Tell us a bit about yourself"
                    className="min-h-[100px] resize-none"
                  />
                )}
              />
            </FormField>
          </Card>
        </motion.div>

        {/* Email and Phone */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.08 }}
          className="grid grid-cols-1 gap-4 lg:grid-cols-2"
        >
          <Card>
            <SettingsCardTitle icon={Mail}>Email address</SettingsCardTitle>

            <FormField
              label="Email address"
              error={errors.email?.message || state.error}
            >
              <div className="flex items-baseline gap-2">
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ""}
                      type="email"
                      placeholder="Enter your email address"
                      className="flex-1"
                      error={errors.email?.message}
                    />
                  )}
                />
                <Button
                  type="button"
                  size="sm"
                  loading={isProcessing}
                  onClick={handleSendOtp}
                >
                  Update
                </Button>
              </div>
            </FormField>

            {/* OTP Section */}
            <div id="clerk-captcha" className="w-full" />
            <div
              className={cn(
                "transition-[max-height,opacity] duration-200 ease-in-out",
                {
                  "max-h-0 overflow-hidden opacity-0": !showOtpField,
                  "max-h-fit opacity-100": showOtpField,
                },
              )}
            >
              <div className="flex flex-col gap-4">
                <OtpInput
                  value={state.code}
                  onChange={handleOtpChange}
                  onComplete={handleOtpComplete}
                  error={state.otpError}
                  label="Check your email"
                  description={`We sent a code to ${watch("email")}. Enter 6 digit code that mentioned in the email`}
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSendOtp}
                  disabled={!state.canResend}
                >
                  {state.canResend
                    ? "Resend code"
                    : `Resend code (${state.resendTimer}s)`}
                </Button>
              </div>
            </div>
          </Card>

          <Card>
            <SettingsCardTitle icon={Phone}>Phone number</SettingsCardTitle>

            <FormField error={errors.phoneNumber?.message || phoneError}>
              <div className="flex flex-row items-baseline gap-2">
                <Controller
                  name="phoneNumber"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ""}
                      type="tel"
                      placeholder="Enter your phone number"
                      className="flex-1"
                      error={errors.phoneNumber?.message}
                    />
                  )}
                />
                <Button
                  type="button"
                  size="sm"
                  loading={isPhoneProcessing}
                  disabled={isPhoneProcessing}
                  onClick={handleUpdatePhone}
                >
                  Update
                </Button>
              </div>
            </FormField>
          </Card>
        </motion.div>

        {/* Account Deactivation */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.16 }}
        >
          <CloseAccountCard userId={userId} />
        </motion.div>

        <Controller
          name="clerkUserId"
          control={control}
          render={({ field }) => (
            <Input {...field} value={field.value || ""} type="hidden" />
          )}
        />

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="flex justify-end"
        >
          <Button
            type="submit"
            loading={updateProfileMutation.isPending}
            disabled={updateProfileMutation.isPending}
            variant="primary"
            className="gap-1.5"
          >
            Save changes
          </Button>
        </motion.div>
      </form>

      <UploadAvatarModal
        isOpen={isUploadModalOpen}
        onClose={handleUploadClose}
        onSave={handleUploadSave}
      />
    </>
  );
}
